/**
 * Milestone & Growth Pulse Report Templates
 * Based on best practices from Duolingo, Strava, LinkedIn
 */

import { PulseReportTemplate } from "./types";

export const MILESTONE_ACHIEVEMENT: PulseReportTemplate = {
  id: "milestone-achievement",
  name: "Milestone Achievement",
  reportType: "analytics",
  description: "Celebrate user milestones and achievements",
  purpose:
    "Recognize and celebrate user achievements to increase engagement and loyalty.",
  icon: "TrophyOutlined",
  color: "#f59e0b",
  suggestedHtmlTemplateId: "milestone-achievement",
  suggestedSubject: "Congratulations! You've reached {{milestone_name}}",
  defaultSchedule: {
    type: "on_event",
    eventType: "milestone_reached",
  },
  defaultDataSections: {
    billing: false,
    usage: true,
    performance: true,
    analytics: false,
    team: false,
  },
  targetAudience: "Users who achieve specific milestones",
  expectedOutcomes: [
    "Increased user engagement",
    "Higher retention rates",
    "Social sharing of achievements",
    "Positive brand association",
  ],
  bestPractices: [
    "Celebrate immediately when milestone hit",
    "Make it shareable on social media",
    "Show progress toward next milestone",
    "Include exclusive rewards if possible",
  ],
  keyMetrics: [
    "Milestone name",
    "Achievement stats",
    "User percentile",
    "Next milestone",
    "Journey progress",
  ],
  tags: ["milestone", "achievement", "celebration", "gamification", "engagement"],
  bestFor: ["User retention", "Engagement campaigns", "Loyalty programs"],
  difficulty: "intermediate",
  setupTime: "10 minutes",
  previewDescription: "Celebratory achievement notification with badge",
};

export const ACCOUNT_HEALTH_SCORE: PulseReportTemplate = {
  id: "account-health-score",
  name: "Account Health Score",
  reportType: "analytics",
  description: "Overall account health with recommendations",
  purpose:
    "Provide users with a clear health score and actionable improvement suggestions.",
  icon: "HeartOutlined",
  color: "#10b981",
  suggestedHtmlTemplateId: "account-health-score",
  suggestedSubject: "Your Account Health Score: {{health_score}}/100",
  defaultSchedule: {
    type: "monthly",
    dayOfMonth: 15,
    time: "10:00",
  },
  defaultDataSections: {
    billing: true,
    usage: true,
    performance: true,
    analytics: true,
    team: false,
  },
  targetAudience: "Account owners at risk of churn",
  expectedOutcomes: [
    "Proactive churn prevention",
    "Increased feature adoption",
    "Clear action items for users",
    "Improved customer success metrics",
  ],
  bestPractices: [
    "Break down score by category",
    "Provide 3 quick wins to improve",
    "Color-code health status",
    "Link to specific improvement actions",
  ],
  keyMetrics: [
    "Overall health score",
    "Setup completion",
    "Engagement score",
    "Performance score",
    "Top recommendations",
  ],
  tags: ["health", "score", "recommendations", "proactive", "churn"],
  bestFor: ["Customer success", "Churn prevention", "Adoption campaigns"],
  difficulty: "advanced",
  setupTime: "15 minutes",
  previewDescription: "Health score dashboard with improvement tips",
};

export const GROWTH_SUMMARY: PulseReportTemplate = {
  id: "growth-summary",
  name: "Growth Summary Report",
  reportType: "analytics",
  description: "Track growth metrics and trends over time",
  purpose:
    "Showcase platform growth and demonstrate value through increasing metrics.",
  icon: "RiseOutlined",
  color: "#8b5cf6",
  suggestedHtmlTemplateId: "monthly-analytics",
  suggestedSubject: "Your Growth Summary - {{growth_percentage}}% increase",
  defaultSchedule: {
    type: "monthly",
    dayOfMonth: 1,
    time: "09:00",
  },
  defaultDataSections: {
    billing: false,
    usage: true,
    performance: true,
    analytics: true,
    team: false,
  },
  targetAudience: "Business owners and executives",
  expectedOutcomes: [
    "Clear growth visualization",
    "ROI demonstration",
    "Strategic insights",
    "Budget justification data",
  ],
  bestPractices: [
    "Show month-over-month growth",
    "Highlight biggest wins",
    "Compare to goals/targets",
    "Include projection for next period",
  ],
  keyMetrics: [
    "Conversation growth",
    "User growth",
    "Revenue impact",
    "Efficiency gains",
    "Goal progress",
  ],
  tags: ["growth", "trends", "roi", "executive", "summary"],
  bestFor: ["Executive reporting", "Board updates", "ROI tracking"],
  difficulty: "intermediate",
  setupTime: "10 minutes",
  previewDescription: "Growth trends and progress visualization",
};

export const ANNIVERSARY_REPORT: PulseReportTemplate = {
  id: "anniversary-report",
  name: "Account Anniversary Report",
  reportType: "analytics",
  description: "Celebrate account anniversaries with year-in-review",
  purpose:
    "Mark account anniversaries with a retrospective showing their journey and impact.",
  icon: "GiftOutlined",
  color: "#ec4899",
  suggestedHtmlTemplateId: "milestone-achievement",
  suggestedSubject: "Happy Anniversary! Your {{years}} year journey with us",
  defaultSchedule: {
    type: "on_event",
    eventType: "account_anniversary",
  },
  defaultDataSections: {
    billing: true,
    usage: true,
    performance: true,
    analytics: true,
    team: false,
  },
  targetAudience: "Long-term customers on anniversary",
  expectedOutcomes: [
    "Strengthened customer relationship",
    "Increased loyalty",
    "Referral opportunities",
    "Renewal readiness",
  ],
  bestPractices: [
    "Make it personal and celebratory",
    "Show their journey stats",
    "Thank them genuinely",
    "Offer anniversary perks/discounts",
  ],
  keyMetrics: [
    "Total conversations handled",
    "Time saved",
    "Customer satisfaction",
    "Feature adoption",
    "Support tickets avoided",
  ],
  tags: ["anniversary", "celebration", "loyalty", "retention", "milestone"],
  bestFor: ["Customer retention", "Loyalty programs", "Relationship building"],
  difficulty: "intermediate",
  setupTime: "10 minutes",
  previewDescription: "Celebratory year-in-review retrospective",
};

export const MILESTONE_TEMPLATES = [
  MILESTONE_ACHIEVEMENT,
  ACCOUNT_HEALTH_SCORE,
  GROWTH_SUMMARY,
  ANNIVERSARY_REPORT,
];
